import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import { useMemo, useState } from "react";
import ReportingExportButton from "../../../components/Button/ReportingExportButton";
import AuditLogModal from "../../../components/Modal/AuditLogModal";
import AuditPDFTemplate from "../../../components/PDF/Reporting/audit/AuditPDFTemplate";
import ReportItemsPanel from "../../../components/Reporting/items/ReportItemsPanel";
import ReportCreatedLine from "../../../components/Reporting/ReportCreatedLine";
import {
	getReviewerFact,
	getTenantFact,
} from "../../../components/Reporting/reportFacts";
import ReportHeader, {
	ReportHeaderFact,
} from "../../../components/Reporting/ReportHeader";
import { useGlobalLoading } from "../../../hooks/useGlobalLoading";
import { useLookup } from "../../../hooks/useLookup";
import useReportDocumentsDrawer from "../../../hooks/useReportDocumentsDrawer";
import useReportEditor from "../../../hooks/useReportEditor";
import useReportItemNames from "../../../hooks/useReportItemNames";
import { getAuditsList } from "../../../services/api-server/audits";
import { GetAntIcon } from "../../../utils/ant_icons";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import { getAuditTypeLabel } from "../../../utils/reporting/auditTypes";
import { createAuditLookupConfig } from "../../../utils/reporting/lookupConfigs/audit";
import { getReportAuthorEmail } from "../../../utils/reporting/reportAuthor";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import { getReportStatusDisplay } from "../../../utils/reporting/statusDisplay";
import { handleExportReport } from "../../../utils/reports";

interface AuditOverviewProps {
	reportId: string;
}

function AuditOverview({ reportId }: Readonly<AuditOverviewProps>) {
	const [auditLogOpen, setAuditLogOpen] = useState(false);

	// Fetching data based on reportId
	const { data, isLoading } = useQuery({
		queryKey: ["report-overview", reportId],
		queryFn: async () => {
			const response: any = await getAuditsList({ _id: reportId });
			return response[0] ?? null;
		},
		enabled: !!reportId,
	});

	const editor = useReportEditor("audit", data, ["report-overview", reportId]);
	const documents = useReportDocumentsDrawer("audit", reportId, data);

	useGlobalLoading("audit-overview", isLoading);

	const isRejected = data?.is_rejected === true;
	const statusDisplay = getReportStatusDisplay(
		data?.status,
		data?.open_status,
		isRejected
	);

	const lookupConfig = useMemo(
		() => createAuditLookupConfig(),
		[data?.operator]
	);

	const { dataWithLookup, isLoading: isLoadingLookup } = useLookup(
		["lookup", "report-overview", reportId],
		data ? [data] : [],
		lookupConfig,
		0
	);
	const lookupData = dataWithLookup[0];

	const { emailToName, reportWithNames, isLoadingNames } = useReportItemNames(
		["audit-findings-responsible", reportId],
		lookupData,
		REPORT_ITEM_KINDS.findings
	);

	const facts: ReportHeaderFact[] = [
		{
			key: "audit_type",
			label: "Audit type",
			value: getAuditTypeLabel(data?.audit_type),
		},
		{
			key: "date_of_audit",
			label: "Date of audit",
			value: formatDateDdMmmYyyy(data?.date_and_time_of_audit),
		},
		{ key: "department", label: "Department", value: data?.department ?? "—" },
		// The form only asks for a customer on global audits.
		...(data?.internal_report
			? []
			: [{ key: "customer", label: "Customer", value: data?.customer ?? "—" }]),
		{ key: "auditee", label: "Auditee", value: data?.auditee ?? "—" },
		getReviewerFact(lookupData, isLoadingLookup),
		getTenantFact(data?.operator),
	];

	const created = (
		<ReportCreatedLine
			submittedOn={data?.submitted_on}
			authorName={lookupData?.author_name}
			authorEmail={getReportAuthorEmail(data)}
			historyTitle="Audit review history"
			historyData={lookupData ?? data}
			onOpenAuditLog={() => setAuditLogOpen(true)}
		/>
	);

	return (
		<>
			{data && (
				// Keyed by audit, like the findings panel: another audit opened in
				// place starts expanded, with its summary clamped.
				<ReportHeader
					key={reportId}
					label="Audit details"
					reportNumber={data.report_number}
					isInternal={Boolean(data.internal_report)}
					title={data.title}
					status={statusDisplay}
					actions={
						<>
							<Button icon={GetAntIcon("file2")} onClick={documents.open}>
								Additional documents
							</Button>
							<ReportingExportButton
								type="default"
								onExport={() =>
									handleExportReport(reportWithNames, "audit", AuditPDFTemplate)
								}
								isLoading={isLoadingLookup || isLoadingNames}
							/>
						</>
					}
					created={created}
					facts={facts}
					summaries={[{ key: "summary", label: "Summary", text: data.summary }]}
				>
					{/* Keyed by audit, so another audit opened in place starts with
					    its own filters and open rows. */}
					<ReportItemsPanel
						key={reportId}
						kind={REPORT_ITEM_KINDS.findings}
						items={reportWithNames?.findings ?? []}
						emailToName={emailToName}
						report={data}
						canEditItem={editor.canEditItem}
						onEditItem={editor.openItem}
						onAddItems={editor.canEdit ? editor.open : undefined}
					/>
				</ReportHeader>
			)}
			{/* Outside the header, so collapsing it never closes the log. */}
			{data && (
				<AuditLogModal
					auditlogFilter={{ level: "audit", level_id: data._id }}
					label="Audit"
					type="reporting"
					open={auditLogOpen}
					handleClose={() => {
						setAuditLogOpen(false);
					}}
				/>
			)}

			{documents.drawer}
			{editor.drawer}
		</>
	);
}

export default AuditOverview;
